/**
 * Eyes, as pixel patterns stamped onto the face.
 *
 * An eye is a few pixels. Rendered from geometry it comes out a different few pixels every frame,
 * so the eyes are stamped on (raster.ts PixelStamp) at the point of the face where the skeleton puts
 * them, and the head's actual turn -- not the facing name -- picks the pattern: both eyes from the
 * front, the near one in profile, none from behind.
 *
 * Placement is calibrated to the LPC body (eye rows 29-31, columns 26-29 / 34-37 from the front);
 * the LOOK is a style. The styles, their colours and where they sit are content
 * (`content/game/sprites/ember/marks.json`): `lpc` is LPC's own eye, read off
 * body/male/human/light.png; `ember`, the default, is Ember's, so an Ember face is not mistaken for
 * an LPC one.
 *
 * Pattern characters name colours (marks.json eyeLetters, a style's own `colors`): n brow, # lash,
 * c cool glint, a ember glint, w white, i iris, p pupil, e the dark of an Ember eye; "." leaves the
 * skin alone.
 */
import { bindContent } from "../content/catalog"
import { noteEmberContent } from "./model"
import { EYE, Material } from "./palette"
import { DEFAULT_RASTER, FACING_YAW, Facing, PixelStamp, RasterOptions, cameraAxes, projectPoint } from "./raster"
import { Proportions, Skeleton } from "./rig"
import { Vec3, add, apply, dot, mul, rotY } from "./vec"

/**
 * A colour a pattern letter paints: `skin.<step>` a step of the face's skin ramp (light, base,
 * shade, deep, line, outline), `eye.<name>` one of the eye colours (palettes.json eye), or `#rrggbb`.
 */
export type EyeColor = string

export interface EyeStyle {
    about?: string
    /** The screen-left and screen-right eye seen from the front, and the near eye looking left. */
    front: [string[], string[]]
    profile: string[]
    /** Where the eye's centre falls inside the pattern, in pixels from its top-left. */
    center: { x: number; y: number }
    /** Its own letters' colours, over the shared ones (marks.json eyeLetters): iris, pupil, an Ember eye's dark. */
    colors?: Record<string, EyeColor>
}

/** content's marks.json, the eyes' half of it (marks.ts reads the muscle marks). */
export interface EmberEyeFile {
    eyeLetters: Record<string, EyeColor>
    eyePlacement: { frontTurn: number; profileTurn: number; faceDepth: number; cheek: number }
    defaultEyes: string
    eyeStyles: Record<string, EyeStyle>
}

/** The eye styles, by name (marks.json eyeStyles), filled in place when content is bound. */
export const EYE_STYLES: Record<string, EyeStyle> = {}
export let DEFAULT_EYE_STYLE = "ember"
/** Where eyes sit and when they show (marks.json eyePlacement). */
const PLACEMENT = { frontTurn: 0.5, profileTurn: 0.6, faceDepth: 0.97, cheek: 0.8 }
/** Each style's letters with how to colour them: from the face's skin, or fixed. */
const LETTERS = new Map<string, [string, (skin: Material) => number][]>()

const colorOf = (ref: EyeColor): ((skin: Material) => number) => {
    if (/^#[0-9a-fA-F]{6}$/.test(ref)) { const c = parseInt(ref.slice(1), 16); return () => c }
    const [table, name] = ref.split(".")
    if (table === "skin") return skin => skin[name as keyof Material]
    const c = EYE[name]
    if (table !== "eye" || c === undefined) throw new Error(`Unknown eye colour ${ref}`)
    return () => c
}

/** Fill the eye styles from content's marks.json (bound below). */
export const registerEyeStyles = (file: EmberEyeFile | undefined): void => {
    for (const k of Object.keys(EYE_STYLES)) delete EYE_STYLES[k]
    LETTERS.clear()
    for (const [name, style] of Object.entries(file?.eyeStyles ?? {})) {
        EYE_STYLES[name] = style
        const letters = { ...(file?.eyeLetters ?? {}), ...(style.colors ?? {}) }
        LETTERS.set(name, Object.entries(letters).filter(([letter]) => letter !== "about").map(([letter, ref]) => [letter, colorOf(ref)]))
    }
    DEFAULT_EYE_STYLE = file?.defaultEyes ?? "ember"
    Object.assign(PLACEMENT, file?.eyePlacement ?? {})
    noteEmberContent("eyes", file && { eyeLetters: file.eyeLetters, eyePlacement: file.eyePlacement, defaultEyes: file.defaultEyes, eyeStyles: file.eyeStyles })
}

const mirror = (rows: string[]) => rows.map(row => row.split("").reverse().join(""))

export const faceStamps = (
    sk: Skeleton,
    p: Proportions,
    skin: Material,
    facing: Facing,
    styleName: string = DEFAULT_EYE_STYLE,
    o: RasterOptions = DEFAULT_RASTER,
): PixelStamp[] => {
    const style = EYE_STYLES[styleName] ?? EYE_STYLES[DEFAULT_EYE_STYLE]
    if (!style) return []
    const yaw = rotY(FACING_YAW[facing])
    const { right, back } = cameraAxes(o.pitch)
    const head = mul(yaw, sk.headBasis)
    const forward = apply(head, [0, 0, 1])
    const toward = dot(forward, back)
    const sideways = dot(forward, right)
    const palette: Record<string, number> = {}
    for (const [letter, color] of LETTERS.get(EYE_STYLES[styleName] ? styleName : DEFAULT_EYE_STYLE) ?? []) palette[letter] = color(skin)
    // A point on the head, in head-local coordinates, to frame pixels.
    const at = (local: Vec3) => projectPoint(apply(yaw, add(sk.head, apply(sk.headBasis, local))), o)
    const stamp = (local: Vec3, rows: string[]): PixelStamp => {
        const q = at(local)
        return { x: Math.round(q.x - style.center.x), y: Math.round(q.y - style.center.y), rows, palette, group: "head" }
    }
    const [a, , c] = p.head
    const front = c * PLACEMENT.faceDepth

    if (toward > PLACEMENT.frontTurn) {
        const eyes = [-1, 1].map(s => {
            const local: Vec3 = [s * p.eyeSpacing, p.eyeHeight, front]
            return { local, x: at(local).x }
        })
        eyes.sort((l, r) => l.x - r.x)
        return [stamp(eyes[0].local, style.front[0]), stamp(eyes[1].local, style.front[1])]
    }
    if (toward > 0 && Math.abs(sideways) > PLACEMENT.profileTurn) {
        // The near eye is on whichever side of the head faces the camera.
        const near = dot(apply(head, [1, 0, 0]), back) > 0 ? 1 : -1
        // The side facings stand three-quarters on (raster.ts SIDE_TURN), so the near eye is on the
        // face, toward its near cheek; profileEyeInset sets it back from the face's front edge.
        const local: Vec3 = [near * a * PLACEMENT.cheek, p.eyeHeight, front - p.profileEyeInset]
        const side = stamp(local, sideways < 0 ? style.profile : mirror(style.profile))
        // On the rows the eyes would be on from the front, in this same pose: that spot on a turned
        // face is nearer the camera, and the tilt would otherwise drop a side eye a row below the
        // front ones. LPC keeps them level across facings.
        const level = projectPoint(add(sk.head, [0, p.eyeHeight, front]), o)
        return [{ ...side, y: Math.round(level.y - style.center.y) }]
    }
    return []
}

bindContent(catalog => (catalog.source as any).emberMarks, registerEyeStyles)
